import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resellerService, branchService, customerService } from '../services/api';
import { toast } from 'react-toastify';
import Table from '../components/Table';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import { FiPlus, FiEdit, FiTrash2, FiPause, FiPlay, FiGitBranch, FiKey, FiUser, FiEye, FiMail, FiPhone, FiBriefcase, FiShield, FiCalendar, FiPackage, FiClock, FiUsers } from 'react-icons/fi';

// Short human-readable date used in the reseller/merchant tables.
// Falls back to a dash so blank cells stay visually tidy.
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

// Colour + label for a subscription status pill.
const subscriptionPill = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'active')    return { bg: 'bg-green-100',  color: 'text-green-700',  label: 'Active' };
  if (s === 'trial')     return { bg: 'bg-blue-100',   color: 'text-blue-700',   label: 'Trial' };
  if (s === 'expired')   return { bg: 'bg-red-100',    color: 'text-red-700',    label: 'Expired' };
  if (s === 'cancelled') return { bg: 'bg-gray-200',   color: 'text-gray-700',   label: 'Cancelled' };
  return { bg: 'bg-gray-100', color: 'text-gray-600', label: status || 'Unknown' };
};

const Resellers = () => {
  const navigate = useNavigate();
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentReseller, setCurrentReseller] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    contactPerson: { name: '', email: '', phone: '' },
    companyAddress: { street: '', city: '', state: '', country: '', zipCode: '' },
    maxCustomers: 100,
    maxBranchesPerCustomer: 10,
  });

  const [showBranchesModal, setShowBranchesModal] = useState(false);
  const [branchesReseller, setBranchesReseller] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordBranch, setPasswordBranch] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewReseller, setViewReseller] = useState(null);
  const [viewCustomers, setViewCustomers] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState({});

  useEffect(() => { fetchResellers(); }, []);

  const fetchResellers = async () => {
    try {
      const res = await resellerService.getAll();
      setResellers(res.data.data);
    } catch { toast.error('Failed to fetch resellers'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await resellerService.update(currentReseller._id, formData);
        toast.success('Reseller updated');
      } else {
        await resellerService.create(formData);
        toast.success('Reseller created — welcome email sent!');
      }
      setShowModal(false);
      resetForm();
      fetchResellers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (r) => {
    setCurrentReseller(r);
    setFormData({
      companyName: r.companyName,
      companyEmail: r.companyEmail,
      companyPhone: r.companyPhone || '',
      contactPerson: r.contactPerson || { name: '', email: '', phone: '' },
      companyAddress: r.companyAddress || { street: '', city: '', state: '', country: '', zipCode: '' },
      maxCustomers: r.maxCustomers,
      maxBranchesPerCustomer: r.maxBranchesPerCustomer,
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reseller?')) return;
    try {
      await resellerService.delete(id);
      toast.success('Reseller deleted');
      fetchResellers();
    } catch { toast.error('Failed to delete reseller'); }
  };

  const handleSuspend = async (r) => {
    try {
      await resellerService.suspend(r._id, 'Manual suspension');
      toast.success(r.isSuspended ? 'Reseller activated' : 'Reseller suspended');
      fetchResellers();
    } catch { toast.error('Failed to update status'); }
  };

  const handleViewDetails = async (reseller) => {
    setViewReseller(reseller);
    setShowViewModal(true);
    setViewLoading(true);
    setExpandedCustomers({});
    try {
      const [custRes, branchRes] = await Promise.all([
        customerService.getAll({ resellerId: reseller._id, limit: 200 }),
        branchService.getByReseller(reseller._id),
      ]);
      const branchList = branchRes.data.data || [];
      // Group branches by customerId
      const branchMap = {};
      branchList.forEach((b) => {
        const cid = b.customerId?._id || b.customerId;
        if (cid) {
          if (!branchMap[cid]) branchMap[cid] = [];
          branchMap[cid].push(b);
        }
      });
      const customers = (custRes.data.data || []).map((c) => ({
        ...c,
        branches: branchMap[c._id] || [],
      }));
      setViewCustomers(customers);
    } catch {
      toast.error('Failed to load reseller details');
    } finally {
      setViewLoading(false);
    }
  };

  const handleViewBranches = async (reseller) => {
    setBranchesReseller(reseller);
    setShowBranchesModal(true);
    setBranchesLoading(true);
    try {
      const res = await branchService.getByReseller(reseller._id);
      setBranches(res.data.data);
    } catch {
      toast.error('Failed to fetch branches');
    } finally {
      setBranchesLoading(false);
    }
  };

  const handleResetPassword = (branch) => {
    setPasswordBranch(branch);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await branchService.resetManagerPassword(passwordBranch._id, newPassword);
      toast.success('Manager password updated');
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      companyName: '', companyEmail: '', companyPhone: '',
      adminName: '', adminEmail: '', adminPassword: '',
      contactPerson: { name: '', email: '', phone: '' },
      companyAddress: { street: '', city: '', state: '', country: '', zipCode: '' },
      maxCustomers: 100, maxBranchesPerCustomer: 10,
    });
    setEditMode(false);
    setCurrentReseller(null);
  };

  const columns = [
    { header: 'Company', accessor: 'companyName' },
    { header: 'Email', accessor: 'companyEmail' },
    { header: 'Phone', accessor: 'companyPhone' },
    { header: 'Merchants', accessor: 'totalCustomers' },
    {
      header: 'Registered',
      render: (row) => (
        <span className="inline-flex items-center gap-1 text-xs text-gray-600">
          <FiCalendar size={11} className="text-gray-400" />
          {fmtDate(row.createdAt)}
        </span>
      ),
    },
    {
      header: 'Status',
      render: (row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.isActive && !row.isSuspended ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {row.isActive && !row.isSuspended ? 'Active' : 'Suspended'}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => handleViewDetails(row)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Quick View">
            <FiEye size={15} />
          </button>
          <button
            onClick={() => navigate(`/resellers/${row._id}/merchants`)}
            className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            title="Merchants Table (export, print, search)"
          >
            <FiUsers size={15} />
          </button>
          <button onClick={() => handleViewBranches(row)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="View Branches">
            <FiGitBranch size={15} />
          </button>
          <button onClick={() => handleEdit(row)} className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" title="Edit">
            <FiEdit size={15} />
          </button>
          <button onClick={() => handleSuspend(row)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title={row.isSuspended ? 'Activate' : 'Suspend'}>
            {row.isSuspended ? <FiPlay size={15} /> : <FiPause size={15} />}
          </button>
          <button onClick={() => handleDelete(row._id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
            <FiTrash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Resellers</h1>
          <p className="text-sm text-gray-500 mt-0.5">{resellers.length} total reseller{resellers.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }}>
          <FiPlus size={15} /> Add Reseller
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      ) : (
        <Table columns={columns} data={resellers} />
      )}

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={editMode ? 'Edit Reseller' : 'Add Reseller'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Company Info */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Company Information</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Company Name" name="companyName" value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} required />
              <Input label="Company Email" type="email" name="companyEmail" value={formData.companyEmail} onChange={(e) => setFormData({ ...formData, companyEmail: e.target.value })} required disabled={editMode} />
              <Input label="Company Phone" name="companyPhone" value={formData.companyPhone} onChange={(e) => setFormData({ ...formData, companyPhone: e.target.value })} />
              <Input label="Max Merchants" type="number" name="maxCustomers" value={formData.maxCustomers} onChange={(e) => setFormData({ ...formData, maxCustomers: parseInt(e.target.value) })} required />
            </div>
          </div>

          {!editMode && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Admin Account</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Full Name" name="adminName" value={formData.adminName} onChange={(e) => setFormData({ ...formData, adminName: e.target.value })} required />
                <Input label="Email" type="email" name="adminEmail" value={formData.adminEmail} onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })} required />
                <Input label="Password" type="password" name="adminPassword" value={formData.adminPassword} onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })} required />
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Contact Person</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="Name" value={formData.contactPerson.name} onChange={(e) => setFormData({ ...formData, contactPerson: { ...formData.contactPerson, name: e.target.value } })} />
              <Input label="Email" type="email" value={formData.contactPerson.email} onChange={(e) => setFormData({ ...formData, contactPerson: { ...formData.contactPerson, email: e.target.value } })} />
              <Input label="Phone" value={formData.contactPerson.phone} onChange={(e) => setFormData({ ...formData, contactPerson: { ...formData.contactPerson, phone: e.target.value } })} />
            </div>
          </div>

          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <Button type="submit" className="flex-1">{editMode ? 'Update' : 'Create'} Reseller</Button>
            <Button type="button" variant="secondary" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">Cancel</Button>
          </div>
        </form>
      </Modal>
      {/* Branches Modal */}
      <Modal
        isOpen={showBranchesModal}
        onClose={() => { setShowBranchesModal(false); setBranches([]); setBranchesReseller(null); }}
        title={`Branches — ${branchesReseller?.companyName || ''}`}
        size="lg"
      >
        {branchesLoading ? (
          <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
        ) : branches.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <FiGitBranch size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No branches found for this reseller</p>
          </div>
        ) : (
          <div className="space-y-3">
            {branches.map((branch) => (
              <div key={branch._id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 p-2 bg-blue-100 text-blue-600 rounded-lg">
                    <FiGitBranch size={14} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{branch.branchName} <span className="text-xs font-normal text-gray-400">({branch.branchCode})</span></p>
                    {branch.branchEmail && <p className="text-xs text-gray-500">{branch.branchEmail}</p>}
                    {branch.managerId ? (
                      <div className="flex items-center gap-1 mt-1">
                        <FiUser size={11} className="text-purple-500" />
                        <span className="text-xs text-purple-700 font-medium">{branch.managerId.fullName}</span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-500">{branch.managerId.email}</span>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1">No manager assigned</p>
                    )}
                  </div>
                </div>
                {branch.managerId && (
                  <button
                    onClick={() => handleResetPassword(branch)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                    title="Reset manager password"
                  >
                    <FiKey size={12} /> Reset Password
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* View Details Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => { setShowViewModal(false); setViewReseller(null); setViewCustomers([]); }}
        title={`Details — ${viewReseller?.companyName || ''}`}
        size="lg"
      >
        {viewLoading ? (
          <div className="flex items-center justify-center h-40"><div className="spinner" /></div>
        ) : (
          <div className="space-y-4">
            {/* Reseller Overview */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-indigo-400 uppercase tracking-wide mb-2">Reseller Info</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <FiBriefcase size={13} className="text-indigo-400" />
                  <span className="font-medium">{viewReseller?.companyName}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <FiMail size={13} className="text-indigo-400" />
                  <span>{viewReseller?.companyEmail}</span>
                </div>
                {viewReseller?.companyPhone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <FiPhone size={13} className="text-indigo-400" />
                    <span>{viewReseller.companyPhone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600">
                  <FiUser size={13} className="text-indigo-400" />
                  <span>{viewCustomers.length} merchant{viewCustomers.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <FiCalendar size={13} className="text-indigo-400" />
                  <span>Registered: <span className="font-medium">{fmtDate(viewReseller?.createdAt)}</span></span>
                </div>
              </div>
            </div>

            {/* Customers + Branches */}
            {viewCustomers.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <FiBriefcase size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No merchants found under this reseller</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                {viewCustomers.map((customer) => (
                  <div key={customer._id} className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* Customer Header */}
                    <button
                      type="button"
                      onClick={() => setExpandedCustomers((prev) => ({ ...prev, [customer._id]: !prev[customer._id] }))}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                          <FiBriefcase size={13} className="text-purple-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-800 truncate">{customer.businessName}</p>
                            {customer.planId?.planName && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                                <FiPackage size={10} /> {customer.planId.planName}
                              </span>
                            )}
                            {customer.subscriptionStatus && (() => {
                              const pill = subscriptionPill(customer.subscriptionStatus);
                              return (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${pill.bg} ${pill.color}`}>
                                  {pill.label}
                                </span>
                              );
                            })()}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{customer.businessEmail}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs text-gray-400 hidden sm:inline">{customer.branches.length} branch{customer.branches.length !== 1 ? 'es' : ''}</span>
                        <span className={`w-1.5 h-1.5 rounded-full ${customer.isActive && !customer.isSuspended ? 'bg-green-400' : 'bg-red-400'}`} />
                        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expandedCustomers[customer._id] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Customer Detail + Branches (expandable) */}
                    {expandedCustomers[customer._id] && (
                      <div className="p-4 space-y-3 bg-white">
                        {/* Plan + subscription strip */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pb-3 border-b border-gray-100">
                          <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
                            <div className="flex items-center gap-1 text-[10px] text-indigo-500 uppercase font-semibold">
                              <FiPackage size={10} /> Plan
                            </div>
                            <div className="text-xs font-semibold text-gray-800 mt-0.5 truncate">
                              {customer.planId?.planName || '—'}
                            </div>
                          </div>
                          <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                            <div className="flex items-center gap-1 text-[10px] text-blue-500 uppercase font-semibold">
                              <FiCalendar size={10} /> Joined
                            </div>
                            <div className="text-xs font-semibold text-gray-800 mt-0.5">
                              {fmtDate(customer.createdAt)}
                            </div>
                          </div>
                          <div className="p-2 rounded-lg bg-gray-50 border border-gray-100">
                            <div className="flex items-center gap-1 text-[10px] text-gray-500 uppercase font-semibold">
                              <FiShield size={10} /> Status
                            </div>
                            <div className="text-xs font-semibold mt-0.5 capitalize">
                              {customer.subscriptionStatus || '—'}
                            </div>
                          </div>
                          <div className="p-2 rounded-lg bg-amber-50 border border-amber-100">
                            <div className="flex items-center gap-1 text-[10px] text-amber-600 uppercase font-semibold">
                              <FiClock size={10} /> Expires
                            </div>
                            <div className="text-xs font-semibold text-gray-800 mt-0.5">
                              {fmtDate(customer.subscriptionEndsAt || customer.trialEndsAt)}
                            </div>
                          </div>
                        </div>

                        {/* Owner contact strip */}
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 pb-3 border-b border-gray-100">
                          {customer.owner?.name && (
                            <div className="flex items-center gap-1.5">
                              <FiUser size={11} className="text-gray-400" />
                              <span><span className="font-medium">Owner:</span> {customer.owner.name}</span>
                            </div>
                          )}
                          {customer.owner?.email && (
                            <div className="flex items-center gap-1.5">
                              <FiMail size={11} className="text-gray-400" />
                              <span>{customer.owner.email}</span>
                            </div>
                          )}
                          {customer.businessPhone && (
                            <div className="flex items-center gap-1.5">
                              <FiPhone size={11} className="text-gray-400" />
                              <span>{customer.businessPhone}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <FiShield size={11} className="text-gray-400" />
                            <span><span className="font-medium">Limits:</span> {customer.currentPlan?.maxBranches ?? '—'} br · {customer.currentPlan?.maxUsers ?? '—'} users</span>
                          </div>
                        </div>

                        {/* Branches */}
                        {customer.branches.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-2">No branches for this merchant</p>
                        ) : (
                          <div className="space-y-2">
                            {customer.branches.map((branch) => (
                              <div key={branch._id} className="flex items-start justify-between p-3 rounded-lg bg-gray-50 border border-gray-100">
                                <div className="flex items-start gap-2.5">
                                  <div className="mt-0.5 p-1.5 bg-blue-100 text-blue-600 rounded-md">
                                    <FiGitBranch size={12} />
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-gray-800">
                                      {branch.branchName}
                                      <span className="ml-1.5 text-gray-400 font-normal">({branch.branchCode})</span>
                                    </p>
                                    {branch.branchEmail && (
                                      <p className="text-xs text-gray-500 mt-0.5">{branch.branchEmail}</p>
                                    )}
                                    {branch.address?.city && (
                                      <p className="text-xs text-gray-400">{branch.address.city}{branch.address.country ? `, ${branch.address.country}` : ''}</p>
                                    )}
                                    {branch.managerId ? (
                                      <div className="flex items-center gap-1.5 mt-1.5">
                                        <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center">
                                          <FiUser size={10} className="text-purple-600" />
                                        </div>
                                        <div>
                                          <span className="text-xs font-medium text-purple-700">{branch.managerId.fullName}</span>
                                          <span className="text-xs text-gray-400 ml-1">· {branch.managerId.email}</span>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-400 mt-1 italic">No manager assigned</p>
                                    )}
                                  </div>
                                </div>
                                <span className={`mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${branch.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {branch.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reset Manager Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => { setShowPasswordModal(false); setNewPassword(''); }}
        title="Reset Manager Password"
        size="sm"
      >
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Branch: <span className="font-semibold text-gray-800">{passwordBranch?.branchName}</span>
          </p>
          {passwordBranch?.managerId && (
            <p className="text-sm text-gray-600 mt-1">
              Manager: <span className="font-semibold text-gray-800">{passwordBranch.managerId.fullName}</span>
            </p>
          )}
        </div>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Min. 6 characters"
            required
          />
          <div className="flex gap-2 pt-1">
            <Button type="submit" className="flex-1" disabled={passwordLoading}>
              {passwordLoading ? 'Updating…' : 'Update Password'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => { setShowPasswordModal(false); setNewPassword(''); }} className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Resellers;
